// Admin-side typed client for /admin/articles*.
//
// Mirrors backend/internal/articles/store.go via the admin handlers
// in backend/internal/adminapi/articles.go.
//
// Existing admin editorial pages (pages/editorial/*) render off the
// in-memory mock shape (`Article` in lib/admin-mock.ts). The
// `adaptAdminArticle` adapter converts wire → that mock shape so pages
// keep their imports unchanged.

import { api, ApiError } from "./api";
import { getApiBase } from "./config";
import type { Article, AuthorKey } from "./admin-mock";
import type { KindKey, ReviewStateKey } from "@/components/admin-ui";

// ─── wire types ──────────────────────────────────────────────────────────────
//
// Backend ArticleState includes "rejected" + "archived" + "draft"
// which the admin queue UI already knows how to render (ReviewStateKey
// is a superset).

export type ArticleState = ReviewStateKey;

export interface Section {
  kind: "p" | "h2" | "pull" | "callout" | "sources" | "image";
  text?: string; // p / h2 / pull; the object-storage URL for image
  items?: string[];
}

export interface ArticleStats {
  views: number;
  reads: number;
  leads: number;
  comments: number;
}

// AuthorInfo is the joined snippet the backend includes inline on every
// article list/get response so the FE can render name + avatar without
// a second /admin/authors round-trip. Optional only for back-compat
// with internal endpoints that skip the auth_users join.
export interface AuthorInfo {
  id: string;
  email: string;
  name: string;
  editorial_role: "contributor" | "editor";
  role: string;
}

export interface ArticleWire {
  id: string;
  author_id: string;
  // author is populated by /admin/articles and /admin/articles/{id}.
  author?: AuthorInfo;
  kind: KindKey;
  title: string;
  dek: string;
  hero_image: string;
  slug?: string | null;
  seo_meta: Record<string, unknown>;
  audience: "public" | "pro" | "members";
  lead_capture: boolean;
  tags: string[];
  body: Section[];
  word_count: number;
  read_time_secs: number;
  state: ArticleState;
  reviewer_id?: string | null;
  flags: number;
  version: number;
  submitted_at?: string | null;
  claimed_at?: string | null;
  scheduled_for?: string | null;
  published_at?: string | null;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
  stats: ArticleStats;
}

export interface RevisionWire {
  id: string;
  article_id: string;
  version: number;
  title: string;
  dek: string;
  body: Section[];
  saved_by: string;
  saved_at: string;
}

export interface ReviewEventWire {
  id: string;
  article_id: string;
  kind: string;
  actor_kind: "author" | "admin" | "system";
  actor_id: string;
  body: string;
  anchor: string;
  created_at: string;
}

export interface QueueStats {
  awaiting_review: number;
  in_review: number;
  changes_requested: number;
  published: number;
  scheduled: number;
  rejected: number;
}

// ─── feature flag ────────────────────────────────────────────────────────────

export function articlesApiEnabled(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const env = (import.meta as any).env ?? {};
  return env.VITE_ARTICLES_API === "1" || env.VITE_ARTICLES_API === "true";
}

// ─── fetch wrappers ──────────────────────────────────────────────────────────

export interface AdminListFilter {
  state?: ReviewStateKey | "";
  kind?: KindKey | "";
  q?: string;
  reviewer?: string;
  page?: number;
  limit?: number;
}

function buildQS(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.append(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

export interface AdminListResp {
  articles: ArticleWire[];
  total: number;
  page: number;
  limit: number;
}

export async function listAdminArticles(f: AdminListFilter = {}): Promise<AdminListResp> {
  return api.get<AdminListResp>(`/admin/articles${buildQS({ ...f })}`);
}

// DraftingAdmin — the admin who composed a house (DataLab) article.
// Surfaced only on this admin detail endpoint, never on public JSON
// (PROP-56); null for agent-authored articles.
export interface DraftingAdmin {
  id: string;
  name: string;
}

export interface AdminArticleDetailResp {
  article: ArticleWire;
  revisions: RevisionWire[];
  events: ReviewEventWire[];
  drafted_by?: DraftingAdmin | null;
}

export async function getAdminArticle(id: string): Promise<AdminArticleDetailResp> {
  return api.get<AdminArticleDetailResp>(`/admin/articles/${encodeURIComponent(id)}`);
}

export async function getQueueStats(): Promise<QueueStats> {
  return api.get<QueueStats>(`/admin/articles/queue/stats`);
}

export async function claimArticle(id: string): Promise<ArticleWire> {
  return api.post<ArticleWire>(`/admin/articles/${encodeURIComponent(id)}/claim`);
}

export async function releaseArticle(id: string): Promise<ArticleWire> {
  return api.post<ArticleWire>(`/admin/articles/${encodeURIComponent(id)}/release`);
}

export async function requestChanges(id: string, note: string): Promise<ArticleWire> {
  return api.post<ArticleWire>(`/admin/articles/${encodeURIComponent(id)}/request-changes`, { note });
}

export async function rejectArticle(id: string, note: string): Promise<ArticleWire> {
  return api.post<ArticleWire>(`/admin/articles/${encodeURIComponent(id)}/reject`, { note });
}

export interface ApproveInput {
  publish: "now" | "schedule";
  scheduled_for?: string;
  slug?: string;
  seo_meta?: Record<string, unknown>;
}

export async function approveArticle(id: string, body: ApproveInput): Promise<ArticleWire> {
  return api.post<ArticleWire>(`/admin/articles/${encodeURIComponent(id)}/approve`, body);
}

export async function addNote(id: string, body: string, anchor = ""): Promise<void> {
  await api.post<void>(`/admin/articles/${encodeURIComponent(id)}/note`, { body, anchor });
}

// ─── DataLab compose (PROP-52) ───────────────────────────────────────────────
//
// The admin-side authoring surface. createAdminArticle / patchAdminArticle /
// submitAdminArticle hit the backend handlers in adminapi/articles.go, which
// route every write through the house identity (constants.HouseAuthorID) —
// the resulting article carries the "ProProp Data Lab" byline and submitting
// lands it in the same ReviewQueue as agent articles.

export interface CreateInput {
  kind: KindKey;
  title?: string;
  dek?: string;
}

// PatchInput is the sparse-update payload. An omitted field is left
// unchanged server-side; this client always sends the full editable set
// from the composer, which is fine — re-sending an unchanged value is a
// no-op write.
export interface PatchInput {
  title?: string;
  dek?: string;
  hero_image?: string;
  kind?: KindKey;
  body?: Section[];
  tags?: string[];
  audience?: "public" | "pro" | "members";
  lead_capture?: boolean;
  slug?: string;
  seo_meta?: Record<string, unknown>;
}

export async function createAdminArticle(body: CreateInput): Promise<ArticleWire> {
  return api.post<ArticleWire>(`/admin/articles`, body);
}

export async function patchAdminArticle(id: string, patch: PatchInput): Promise<ArticleWire> {
  return api.patch<ArticleWire>(`/admin/articles/${encodeURIComponent(id)}`, patch);
}

export async function submitAdminArticle(id: string): Promise<ArticleWire> {
  return api.post<ArticleWire>(`/admin/articles/${encodeURIComponent(id)}/submit`);
}

// uploadAdminArticleImage POSTs a multipart image to the article's
// image endpoint and returns the stored object-storage URL — used by
// Compose for the cover image and body-image inserts (PROP-53). The
// shared `api` helper only speaks JSON, so this uses a raw fetch with
// FormData; the session cookie still rides along via credentials.
export async function uploadAdminArticleImage(id: string, file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const resp = await fetch(`${getApiBase()}/admin/articles/${encodeURIComponent(id)}/image`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  const text = await resp.text();
  let data: { url?: string; error?: string } = {};
  if (text) {
    try { data = JSON.parse(text); } catch { /* non-JSON */ }
  }
  if (!resp.ok || !data.url) {
    throw new ApiError(resp.status, data.error || resp.statusText || "image upload failed");
  }
  return data.url;
}

// ─── adapter: wire → admin-mock Article ──────────────────────────────────────
//
// The admin pages page (ArticlesList, ArticleReview, ReviewQueue) read
// from the `Article` shape in lib/admin-mock.ts. Adapting at the
// boundary keeps the page code unchanged.
//
// AuthorKey is currently a string-literal union (clarissa | marcus | …)
// because the mock seeded a tiny author table. Real authors come from
// auth_users and have free-form ids — we cast through `as AuthorKey`
// where the consumer page only needs the id to look up an avatar
// (Authors table likewise becomes a network lookup in a follow-up).

// TINT_POOL + pickTint mirror authors-hooks.ts so the avatar colour is
// stable for a given author id across both pages.
const TINT_POOL = ["#1d4a3a", "#6b5320", "#2a4a66", "#7a2d1f", "#3a2e7a"];
function pickTint(id: string): string {
  let sum = 0;
  for (const c of id) sum = (sum + c.charCodeAt(0)) % TINT_POOL.length;
  return TINT_POOL[sum];
}
function makeInitials(name: string, email: string): string {
  const src = (name || "").trim() || (email || "").split("@")[0];
  if (!src) return "??";
  const parts = src.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function adaptAdminArticle(
  w: ArticleWire,
  reviewerName?: string,
  isYou = false,
): Article {
  // authorDisplay is built from the joined `author` field the backend
  // includes on list/get. Falls back to the AUTHORS mock map only when
  // the wire has no inline author — covers the offline-mock case.
  let authorDisplay: Article["authorDisplay"] | undefined;
  if (w.author) {
    const name = w.author.name || w.author.email;
    authorDisplay = {
      id: w.author.id,
      email: w.author.email,
      name,
      initials: makeInitials(w.author.name, w.author.email),
      tint: pickTint(w.author.id),
      team: w.author.editorial_role === "editor" ? "Editorial" : "Contributor",
    };
  }
  return {
    id: w.id,
    kind: w.kind,
    title: w.title,
    dek: w.dek,
    author: (w.author_id as unknown) as AuthorKey,
    authorDisplay,
    state: w.state,
    submitted: w.submitted_at ? relativeTime(w.submitted_at) : "—",
    submittedAt: w.submitted_at ?? undefined,
    sla: deriveSla(w),
    version: w.version,
    words: w.word_count,
    readTime: readTimeLabel(w.read_time_secs),
    flags: w.flags,
    comments: w.stats.comments,
    slug: w.slug ?? `/news/${w.id}`,
    reviewer: w.reviewer_id ? (isYou ? "You" : reviewerName ?? w.reviewer_id) : null,
    reviewerId: w.reviewer_id ?? null,
    tags: w.tags ?? [],
    views: w.stats.views || undefined,
    reads: w.stats.reads || undefined,
    leads: w.stats.leads || undefined,
    publishAt: w.scheduled_for ?? w.published_at ?? undefined,
  };
}

// SLA tracker — 48h target from submission. Anything past that without
// a terminal state (published / rejected / archived) is overdue.
// Terminal states never carry an SLA badge.
function deriveSla(w: ArticleWire): "on-time" | "overdue" | "cleared" {
  if (
    w.state === "published" ||
    w.state === "scheduled" ||
    w.state === "rejected" ||
    w.state === "archived"
  ) {
    return "cleared";
  }
  if (!w.submitted_at) return "on-time";
  const submitted = new Date(w.submitted_at).getTime();
  const ageHrs = (Date.now() - submitted) / (3600 * 1000);
  return ageHrs > 48 ? "overdue" : "on-time";
}

function readTimeLabel(secs: number): string {
  if (!secs || secs <= 0) return "1 min";
  const mins = Math.max(1, Math.round(secs / 60));
  return `${mins} min`;
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)} minutes ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} hours ago`;
  if (secs < 86400 * 7) return `${Math.floor(secs / 86400)} days ago`;
  return d.toLocaleDateString("en-SG", { year: "numeric", month: "short", day: "numeric" });
}

// Re-export the typed ApiError so admin pages can `instanceof`-check it
// when displaying error toasts without importing ./api directly.
export { ApiError };
